<template>
  <div
    id="channel-wrapper"
    class="flex-col relative transition-colors duration-200"
    :class="[largeDesktop ? 'channel-grid' : 'flex']"
  >
    <!-- NOTE: need this v-if otherwise it messes with chat data on mobile when it's not opened -->
    <div
      v-if="largeDesktop"
      class="chat-sidebar max-h-dvh sticky top-0 overflow-y-auto px-4 pb-6"
    >
      <ChannelChat />
    </div>

    <CommunitiesRow
      v-if="showCommunitiesRow"
      class="py-2 px-4 sticky z-50 top-0 bg-bg-secondary backdrop-blur-3xl inset-x-0 channel-top-row hide-scrollbar"
    />
    <ProfileRow
      v-else-if="userStore.isAuthenticated && !largeDesktop && showProfileRow"
      class="sticky z-50 top-0 bg-bg-secondary backdrop-blur-3xl inset-x-0 channel-top-row hide-scrollbar"
    />

    <main
      id="main-content"
      class="w-full mx-auto channel-page-view pb-10 max-large-desktop:pb-32 flex-1 flex flex-col"
      :class="{ 'overflow-x-auto': isPageContainerHozScrollable }"
    >
      <ConnectWalletWebThreeButton
        v-if="canUseTokenGating && !largeDesktop"
        class="max-h-11 flex-1 max-w-[100vw]"
        type="with-balances"
        show-available-tokens
        extended
      />

      <RouterView />

      <AppFooter />
    </main>

    <div
      v-if="largeDesktop"
      class="large-desktop:sticky large-desktop:top-0 z-50 inset-x-0 mx-0 mt-auto max-large-desktop:fixed max-large-desktop:bottom-0 backdrop-blur-3xl large-desktop:m-0 large-desktop:col-[1] large-desktop:row-[1] large-desktop:p-0 overflow-hidden w-full channel-navigation large-desktop:max-h-dvh large-desktop:py-5 large-desktop:max-w-[320px] large-desktop:flex large-desktop:flex-col"
      :class="{
        hidden: hideNavigation,
      }"
    >
      <div
        v-if="!responsiveHelpers.desktop.value && !userStore.isAuthenticated"
        class="bg-brand-bg-main px-4"
      >
        <GetChannelMembershipButton />
      </div>

      <div
        class="flex justify-stretch self-stretch p-4 gap-2 large-desktop:px-8 large-desktop:py-0 text-basics-primary bg-brand-bg-main large-desktop:bg-transparent large-desktop:flex-col large-desktop:bg-none large-desktop:border-none text-base font-serif large-desktop:gap-3 large-desktop:text-left"
      >
        <div
          v-if="userStore.user"
          class="hidden flex-row items-center gap-3 mb-32u overflow-hidden large-desktop:flex"
        >
          <RouterLink
            class="p-5u ring-inset ring-brand-primary ring-[3px] border-[4px] border-brand-quaternary-40 rounded-full hover:ring-8 hover:border-brand-tertiary-60 active:border-brand-secondary-80 transition-all duration-150 flex"
            :to="{
              name: RouteNames.Profile,
              params: { channel: channelStore.channelSlug },
            }"
          >
            <Avatar
              :avatar="{
                name: userStore.username,
                image: userStore.user.avatar,
                id: userStore.user.id,
              }"
              size="md"
            />
          </RouterLink>

          <div class="text-start flex-1">
            <div class="flex flex-row flex-wrap items-center gap-1.5">
              <p
                class="font-serif shrink-0 text-base text-basics-primary uppercase break-good line-clamp-2 whitespace-pre-line leading-snug w-fit"
              >
                {{ userStore.username }}
              </p>

              <span class="inline-flex flex-row gap-2 shrink-0 items-center">
                <CountryBadge
                  :icon="userStore.user.country_iso"
                  class="inline"
                />
                <PremiumBadge
                  v-if="channelStore.isSuperfan"
                  size="sm"
                  class="inline"
                />
              </span>
            </div>

            <p
              v-if="channelStore.isSuperfan"
              class="font-sans text-xs leading-relaxed"
            >
              {{ $t("channel.membership.vip") }}
            </p>
          </div>
        </div>

        <template v-if="largeDesktop && canUseTokenGating">
          <ConnectWalletWebThreeButton type="simple" />
        </template>

        <RouterLink
          v-for="button in bottomBarButtons"
          :key="button.icon"
          :to="button.to"
          :data-testid="`nav-${button.text.toLowerCase()}`"
          class="flex flex-1 flex-col large-desktop:flex-row items-center large-desktop:items-center justify-center large-desktop:justify-start large-desktop:gap-2 whitespace-nowrap uppercase"
        >
          <Icon
            :icon="button.icon"
            class="size-6 inline-block align-middle mb-1"
          />
          <span
            class="text-xs font-serif large-desktop:leading-6 large-desktop:text-base"
            >{{ $t(`channel.navigation.${button.text.toLowerCase()}`) }}</span
          >
        </RouterLink>
      </div>

      <div class="mt-auto max-xl:px-4 px-8 space-y-4">
        <IntercomButton v-if="!appStore.isSimulfest" variant="primary" />

        <div v-if="userStore.user" class="max-large-desktop:hidden">
          <UserAutoTranslateButton />
        </div>
      </div>
    </div>
    <MobileChannelFooter
      v-else-if="route.name === RouteNames.Channel && !largeDesktop"
      @click="chatRoomModalIsOpen = true"
    />
  </div>

  <ChannelChatRoomModal
    v-if="!largeDesktop"
    v-model:is-open="chatRoomModalIsOpen"
  />
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref, toValue } from "vue";
import { useRoute, type RouteLocationRaw } from "vue-router";
import { RouteNames } from "@/router/app.router";
import { Icon } from "@iconify/vue";
import { useUserStore } from "@/stores/user.store";
import PremiumBadge from "./badge/premium-badge.vue";
import CountryBadge from "./badge/country-badge.vue";
import { useChannelStore } from "@/stores/channel.store";
import Avatar from "./me/avatar.vue";
import { useResponsiveHelpers } from "@/composables/global/use-responsive-helpers";
import GetChannelMembershipButton from "./channel/channel-page/get-channel-membership-button.vue";
import CommunitiesRow from "./channel/communities-row.vue";
import ProfileRow from "./channel/profile-row.vue";
import { appBreakpoints } from "@/utils/theme";
import UserAutoTranslateButton from "./me/user-auto-translate-button.vue";
import AppFooter from "./app-footer.vue";

import ChannelChat from "./chat/channel-chat.vue";
import { useAppStore } from "@/stores/app.store";
import { useChatStore } from "@/stores/chat/chat.store";
import ConnectWalletWebThreeButton from "@/components/channel/auth/capabilities/wallet/connect-wallet-button.vue";
import { useChannelCapabilities } from "@/composables/use-channel-capabilities";
import IntercomButton from "./channel/intercom-button.vue";

const MobileChannelFooter = defineAsyncComponent(
  () => import("./mobile-channel-footer.vue"),
);

const ChannelChatRoomModal = defineAsyncComponent(
  () => import("./chat/channel-chat-room-modal.vue"),
);

const appStore = useAppStore();
const route = useRoute();
const userStore = useUserStore();
const channelStore = useChannelStore();
const { largeDesktop } = useResponsiveHelpers();
const { canUseTokenGating } = useChannelCapabilities();

const showCommunitiesRow = computed(
  () =>
    (route.name === RouteNames.Channel &&
      channelStore.hasMultipleCommunities) ||
    channelStore.loadingChannel,
);

const channelPageGridRow = computed(() =>
  showCommunitiesRow.value ? "2" : "1 / span 2",
);
const maxWidthContent = appBreakpoints.desktop;

const chatRoomModalIsOpen = ref(false);

const isPageContainerHozScrollable = computed(() => {
  switch (route.name) {
    case RouteNames.Channel:
      return true;
    case RouteNames.ShowList:
      return true;
    case RouteNames.ProfileMembership:
      return true;
    default:
      return false;
  }
});

const hideNavigation = computed(() => {
  switch (route.name) {
    case RouteNames.NewShow:
    case RouteNames.ThreadsNew:
    case RouteNames.ThreadsItem:
    case RouteNames.Profile:
    case RouteNames.ProfileEdit:
    case RouteNames.ProfileNotifications:
    case RouteNames.ProfilePrivacy:
    case RouteNames.ProfileMedia:
    case RouteNames.ProfileEvents:
    case RouteNames.ProfilePurchases:
      return true;
    default:
      return false;
  }
});

const showProfileRow = computed(() => {
  switch (route.name) {
    case RouteNames.Profile:
    case RouteNames.ProfileMembership:
    case RouteNames.ProfileEdit:
    case RouteNames.ProfileNotifications:
    case RouteNames.ProfilePrivacy:
    case RouteNames.ProfileMedia:
    case RouteNames.ProfileEvents:
    case RouteNames.ProfilePurchases:
      return false;
    default:
      return true;
  }
});

const responsiveHelpers = useResponsiveHelpers();
const bottomBarButtons = computed(() => {
  const buttons: {
    icon: string;
    to: RouteLocationRaw;
    text: string;
  }[] = [
    {
      icon: "ix:home-filled",
      to: {
        name: RouteNames.Channel,
        params: { channel: channelStore.channelSlug },
        hash: "#home",
      },
      text: "home",
    },
    {
      icon: "mdi:controller",
      to: {
        name: RouteNames.Channel,
        params: { channel: channelStore.channelSlug },
        hash: "#contests",
      },
      text: "contests",
    },
    {
      icon: "mdi:play",
      to: {
        name: RouteNames.Channel,
        params: { channel: channelStore.channelSlug },
        hash: "#shows",
      },
      text: "shows",
    },
    {
      icon: "mdi:comments",
      to: {
        name: RouteNames.Channel,
        params: { channel: channelStore.channelSlug },
        hash: "#threads",
      },
      text: "threads",
    },
  ];

  if (userStore.isAuthenticated && !toValue(responsiveHelpers.largeDesktop)) {
    buttons.push({
      icon: "mdi:account",
      to: {
        name: RouteNames.Profile,
        params: { channel: channelStore.channelSlug },
      },
      text: "profile",
    });
  }

  return buttons;
});

const _chatStore = useChatStore();
</script>

<style scoped>
#channel-wrapper {
  background: var(--brand-bg-main, #0c1217);
}

.channel-grid {
  display: grid;
  place-content: start;
  grid-template-columns: 1fr 3fr 1fr;
  grid-template-rows: auto 1fr;
}

:deep(.channel-top-row) {
  grid-row: 1;
  grid-column: 2;
  width: 100%;
}

.channel-navigation,
.chat-sidebar {
  grid-row: 1 / span 2;
}

.channel-navigation {
  grid-column: 1;
}

.channel-page-view {
  grid-row: v-bind(channelPageGridRow);
  grid-column: 2;
}

.chat-sidebar {
  grid-column: 3;
}

:deep(.channel-top-row),
.channel-page-view {
  max-width: v-bind(maxWidthContent);
  margin: 0 auto;
}
</style>
